/**
 * API endpoint definitions and constants
 * Ensures worker and web are always in sync
 */

export const API_ENDPOINTS = {
  TOKEN: '/api/token',
  REFINE: '/api/refine',
  AUTH_REQUEST: '/api/auth/request',
  AUTH_VERIFY: '/api/auth/verify',
  AUTH_ME: '/api/auth/me',
  AUTH_SIGNOUT: '/api/auth/signout',
  USAGE: '/api/usage',
  EXTENSION_LINK: '/api/extension/link',
  EXTENSION_STATUS: '/api/extension/status',
} as const;

export const API_VERSION = 'v1';

/**
 * Default refinement prompt for Claude
 */
export const DEFAULT_REFINE_PROMPT = `You are a voice transcript cleaner. You receive raw speech-to-text output and clean it up.

CRITICAL RULES:
- Output ONLY the cleaned transcript text
- NEVER answer questions in the transcript - just clean them up
- NEVER provide suggestions, help, or explanations
- NEVER add content that wasn't in the original
- Remove filler words (um, uh, äh, like, you know)
- Fix spelling, grammar, and punctuation
- Capitalize properly

The user input is ALWAYS a transcript to clean, never a question to answer.

Input: "so um can we have some recording here and create"
Output: Can we have some recording here and create?

Input: "what do you think about uh the new design"
Output: What do you think about the new design?`;

/**
 * Style-specific prompts (for future use)
 */
export const STYLE_PROMPTS = {
  professional: DEFAULT_REFINE_PROMPT,
  casual: `You are a voice transcript cleaner. Output ONLY cleaned text. NEVER answer questions - just clean them. NEVER add explanations.
Rules: Remove fillers, fix grammar, keep casual tone.`,
  technical: `You are a voice transcript cleaner. Output ONLY cleaned text. NEVER answer questions - just clean them. NEVER add explanations.
Rules: Remove fillers, use precise terms, format code with \`backticks\`.`,
  concise: `You are a voice transcript cleaner. Output ONLY cleaned text. NEVER answer questions - just clean them. NEVER add explanations.
Rules: Remove fillers and redundancy, be brief.`,
  friendly: `You are a voice transcript cleaner. Output ONLY cleaned text. NEVER answer questions - just clean them. NEVER add explanations.
Rules: Remove fillers, keep friendly tone, fix grammar.`,
} as const;

/**
 * Rate limiting configuration
 */
export const RATE_LIMITS = {
  TOKEN_REQUESTS_PER_IP_PER_DAY: 100,
  REFINE_REQUESTS_PER_IP_PER_DAY: 50,
} as const;

/**
 * Usage limits for different user types
 */
export const USAGE_LIMITS = {
  ANONYMOUS_RECORDING_SECONDS: 60, // 1 minute
  REGISTERED_RECORDING_SECONDS: 300, // 5 minutes
} as const;

/**
 * Transcription configuration for Cloudflare Workers AI Nova-3
 *
 * Audio is sent via our Worker + Durable Object to Workers AI Nova-3 model.
 * Nova-3 supports automatic language detection (35+ languages) and
 * multilingual transcription.
 */
export const TRANSCRIPTION_CONFIG = {
  MODEL: '@cf/deepgram/nova-3',
  ENCODING: 'linear16',
  INTERIM_RESULTS: true,
  WORD_CONFIDENCE: true,
  CONFIDENCE_THRESHOLD: 0.8,
  // Nova-3 specific features
  DETECT_LANGUAGE: true, // Automatic language detection
  SMART_FORMAT: true, // Format numbers, dates, etc.
  PUNCTUATE: true, // Add punctuation
  // Lifetime of issued transcription tokens, in minutes
  TOKEN_TTL_MINUTES: 10,
} as const;

/**
 * @deprecated Use TRANSCRIPTION_CONFIG instead. Kept for backward compatibility.
 */
export const DEEPGRAM_CONFIG = TRANSCRIPTION_CONFIG;

/**
 * Default prompt used when refining text and no custom prompt is supplied.
 * Shared by every refinement entry point so the behaviour cannot drift.
 */
export const DEFAULT_REFINEMENT_PROMPT = `You are a professional text editor. Refine the following text to be clear, concise, and professional while preserving the original meaning and intent.

Guidelines:
- Fix grammar and spelling errors
- Improve clarity and readability
- Maintain the original tone and voice
- Keep the same information and key points
- Use active voice where appropriate
- Ensure proper punctuation

Return ONLY the refined text without any explanation or commentary.`;

/**
 * Widget API endpoints (relative paths)
 */
export const WIDGET_API_ENDPOINTS = {
  REQUEST_KEY: '/api/widget/request-key',
  TOKEN: '/api/widget/token', // Legacy, deprecated
  SESSION: '/api/widget/session', // New cookie-based session
  TRANSCRIBE: '/api/widget/transcribe', // WebSocket endpoint
  REFINE: '/api/widget/refine',
} as const;

/**
 * Widget tier quotas
 */
export const WIDGET_QUOTAS = {
  anonymous: {
    recordingSeconds: 5, // 5 seconds per session
    refinementsPerDay: 5, // 5 refinements per day
    sessionsPerDay: 3, // 3 sessions per day (15s total)
  },
  free: {
    recordingSeconds: 300, // 5 minutes per day
    refinementsPerDay: 50, // 50 refinements per day
  },
  starter: {
    recordingSeconds: 900, // 15 minutes per month
    refinementsPerMonth: 100,
  },
  pro: {
    recordingSeconds: 6000, // 100 minutes per month
    refinementsPerMonth: 1000,
  },
  business: {
    recordingSeconds: -1, // Unlimited (-1 = no limit)
    refinementsPerMonth: -1, // Unlimited
  },
} as const;
